import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import {
  BASE_URL,
  DEFAULT_PAGE_SIZE,
  LOGIN_PAGE_URL,
  X_HEADER_ID_NAME,
} from '@src/utils/constants';
import { idType, selectItemType } from '@src/types';
import { baseQuerySample, headerIdConfigs } from '@store/helpers';
import { isPublicAuthPage, isTrackRoute } from '@utils/auth';
import { sessionSetter } from '@utils/storage';

// TODO this is a template for the supervisor only
const nakedBaseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  credentials: 'include',
});

export const notesApi = createApi({
  reducerPath: 'Notes',
  tagTypes: ['Notes'],
  baseQuery: baseQuerySample,
  endpoints: (builder) => ({
    // TODO keep this until all checks are passed!! and then remove!
    // createOrUpdateNotes: builder.mutation({
    //   query: ({ data, type, id, supervisorId }) => {
    //     const overrideHeaders = supervisorId
    //       ? {
    //           headers: {
    //             'X-User-Id': supervisorId,
    //           },
    //         }
    //       : {};
    //     return {
    //       url: `/v1/notes/${type}/${id ? `update/${id}` : 'create'}`,
    //       method: id ? 'PUT' : 'POST',
    //       body: data,
    //       ...overrideHeaders,
    //     };
    //   },
    // }),

    createOrUpdateNotes: builder.mutation({
      async queryFn(args, _queryApi, _extraOptions, baseQuery) {
        const { data, type, id, supervisorId } = args;

        const fetchArgs: FetchArgs = {
          url: `/v1/notes/${type}/${id ? `update/${id}` : 'create'}`,
          method: id ? 'PUT' : 'POST',
          body: data,
        };

        if (supervisorId) {
          fetchArgs.params = {
            ...fetchArgs.params,
            userId: supervisorId,
          };

          const headers = new Headers();
          const config =
            sessionSetter.getItem<Record<string, any>>(X_HEADER_ID_NAME);

          if (config) {
            Object.entries(config).forEach(([key, value]) => {
              const hKey = headerIdConfigs[key as keyof typeof headerIdConfigs];
              if (hKey && value) {
                const finalValue = key === 'user' ? supervisorId : value;
                headers.set(hKey, String(finalValue));
              }
            });
          }

          fetchArgs.headers = headers;

          const result = await nakedBaseQuery(
            fetchArgs,
            _queryApi,
            _extraOptions
          );

          if (result.error?.status === 401) {
            const pathname = window.location.pathname;
            if (!isPublicAuthPage(pathname) && !isTrackRoute(pathname)) {
              window.location.href = LOGIN_PAGE_URL;
            }
          }
          return result;
        }
        // TODO do not remove this!! this is working by default the baseQuery
        return (
          baseQuery as BaseQueryFn<
            string | FetchArgs,
            unknown,
            FetchBaseQueryError
          >
        )(fetchArgs, _queryApi, _extraOptions);
      },
    }),

    getNoteById: builder.query({
      query: ({ id, type }) => ({
        url: `/v1/notes/${type}/${id}`,
      }),
    }),
    getNotes: builder.mutation({
      query: ({ page, argument, body = {}, params = {} }) => ({
        url: `/v1/notes/${argument?.type}/user`,
        method: 'POST',
        body,
        params: {
          page: page,
          size: DEFAULT_PAGE_SIZE,
          sortBy: 'actionTime',
          sortDir: 'desc',
          ...params,
        },
      }),
    }),
    getNoteCategories: builder.query<Array<selectItemType>, { type: string }>({
      query: ({ type }) => ({
        url: `/v1/notes/${type}/categories`,
      }),
    }),
    getNoteCategoriesForModal: builder.query<
      Array<selectItemType>,
      { modelId?: idType }
    >({
      query: ({ modelId = 1 }) => ({
        url: `/v1/notes/categories/${modelId}`,
      }),
    }),
    getAuditNotesByIdPost: builder.mutation({
      query: ({ id, type }) => ({
        url: `/v1/notes/${type}/${id}`,
        method: 'POST',
      }),
    }),
    getUserNotes: builder.query({
      query: ({
        argument: { userId },
        page = 0,
        size = 5,
        sortBy = 'actionTime',
        sortDir = 'desc',
      }) => ({
        url: `/v1/notes/admin/${userId}?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`,
      }),
    }),
    getNotesByAssociatedId: builder.query({
      query: ({
        type,
        recordId = 1,
        associatedId,
        page = 0,
        size = DEFAULT_PAGE_SIZE,
        sortBy = 'noted',
        sortDir = 'desc',
      }) => ({
        url: `/v1/notes/${type}/${associatedId}/${recordId}?page=${page}&size=${size}&sortBy=${sortBy}&sortDir=${sortDir}`,
      }),
    }),
    createNotesByContact: builder.mutation({
      query: ({ recordId, category, noteDetail }) => ({
        url: `/v1/notes/memberAuthContacts/create`,
        method: 'POST',
        body: {
          recordId,
          category,
          noteDetail,
        },
      }),
    }),
    getNotesByContact: builder.query({
      query: ({
        memberId,
        recordId,
        resource = 'memberAuthContacts',
        argument = {},
        body = {},
        params: {
          page = 0,
          size = DEFAULT_PAGE_SIZE,
          sortBy = 'actionTime',
          sortDir = 'desc',
        } = {},
      }) => ({
        url: `/v1/notes/${resource}/${memberId || argument.memberId}/${recordId || argument.recordId}`,
        method: 'POST',
        body,
        params: {
          page,
          size,
          sortBy,
          sortDir,
        },
      }),
    }),
  }),
});

export const {
  useCreateOrUpdateNotesMutation,
  useLazyGetNoteByIdQuery,
  useGetNotesMutation,
  useGetAuditNotesByIdPostMutation,
  useLazyGetNoteCategoriesQuery,
  useLazyGetNoteCategoriesForModalQuery,
  useLazyGetUserNotesQuery,
  useLazyGetNotesByAssociatedIdQuery,
  useCreateNotesByContactMutation,
  useLazyGetNotesByContactQuery,
} = notesApi;
